import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// ─────────────── Types ───────────────

export type QuickQuoteClientType = "residential" | "commercial" | "rural";
export type QuickQuoteCalcMode = "simplified" | "manual" | "cpq";
export type QuickQuoteProposalStyle = "clean" | "complete";
export type QuickQuoteStatus =
  | "draft"
  | "sent"
  | "viewed"
  | "interested"
  | "promoted"
  | "expired";

export type QuickQuoteCalcBasis = "consumption" | "target";
export type QuickQuoteDiscountType = "percent" | "fixed" | null;
export type QuickQuotePaymentCondition =
  | "cash"
  | "installments_2_5"
  | "installments_6_12"
  | "financing"
  | null;
export type QuickQuoteNegotiationStatus = "negotiating" | null;

export interface QuoteLineItem {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
  type: "equipment" | "service" | "kit";
  product_id: string | null;
  kit_id: string | null;
  // ID da linha do kit pai (quando esta linha é um componente do kit). Opcional/retrocompatível.
  parent_kit_line_id?: string | null;
}

export interface QuickQuote {
  id: string;
  tenant_id: string;
  code: string;
  lead_id: string | null;
  promoted_to_deal_id: string | null;
  promoted_to_proposal_id: string | null;
  client_name: string | null;
  client_phone: string | null;
  client_phone_normalized: string | null;
  client_email: string | null;
  client_city: string | null;
  client_state: string | null;
  client_type: QuickQuoteClientType;
  calc_mode: QuickQuoteCalcMode;
  bill_value: number | null;
  consumption_kwh: number | null;
  connection_type: string | null;
  cip_value: number | null;
  roof_type: string | null;
  power_kwp: number | null;
  panel_count: number | null;
  estimated_generation_kwh: number | null;
  monthly_savings: number | null;
  payback_years: number | null;
  economy_25_years: number | null;
  co2_avoided_kg_year: number | null;
  trees_equivalent: number | null;
  investment_min: number | null;
  investment_max: number | null;
  total_value: number | null;
  kit_items: any[] | null;
  kit_display_name: string | null;
  structured_installments: any[] | null;
  payment_method: string | null;
  payment_observations: string | null;
  proposal_style: QuickQuoteProposalStyle;
  include_financial_analysis: boolean;
  public_slug: string | null;
  validity_days: number;
  expires_at: string | null;
  sent_at: string | null;
  viewed_at: string | null;
  view_count: number;
  client_interested_at: string | null;
  whatsapp_sent_at: string | null;
  email_sent_at: string | null;
  status: QuickQuoteStatus;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // ─── COTACAO-B.2 — novos campos ───
  extra_kwh_target: number | null;
  // ─── COTACAO-C.2 — snapshot de auditoria ───
  panel_watts_used: number | null;
  calc_basis: QuickQuoteCalcBasis;
  line_items: QuoteLineItem[] | null;
  discount_type: QuickQuoteDiscountType;
  discount_value: number;
  subtotal: number | null;
  payment_condition: QuickQuotePaymentCondition;
  warranty_text: string | null;
  observations: string | null;
  internal_notes: string | null;
  bill_photo_url: string | null;
  negotiation_status: QuickQuoteNegotiationStatus;
}

// ─────────────── Labels & colors ───────────────

export const QUOTE_STATUS_LABELS: Record<QuickQuoteStatus, string> = {
  draft: "Rascunho",
  sent: "Enviada",
  viewed: "Visualizada",
  interested: "Com interesse",
  promoted: "Promovida",
  expired: "Expirada",
};

// Tokens semânticos do design system (ver index.css)
export const QUOTE_STATUS_COLORS: Record<QuickQuoteStatus, string> = {
  draft: "bg-muted text-muted-foreground",
  sent: "bg-primary/10 text-primary",
  viewed: "bg-warning/10 text-warning",
  interested: "bg-success/10 text-success",
  promoted: "bg-accent/10 text-accent-foreground",
  expired: "bg-destructive/10 text-destructive",
};

export const CLIENT_TYPE_LABELS: Record<QuickQuoteClientType, string> = {
  residential: "Residencial",
  commercial: "Comercial",
  rural: "Rural",
};

export const CALC_MODE_LABELS: Record<QuickQuoteCalcMode, string> = {
  simplified: "Calculadora automática",
  manual: "Preenchimento manual",
  cpq: "CPQ completo",
};

export const PAYMENT_CONDITION_LABELS: Record<
  Exclude<QuickQuotePaymentCondition, null>,
  string
> = {
  cash: "À vista",
  installments_2_5: "2 a 5x sem juros",
  installments_6_12: "6 a 12x",
  financing: "Financiamento",
};

// ─────────────── Hooks ───────────────

export function useQuickQuotes() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: ["quick-quotes", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quick_quotes" as any)
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as QuickQuote[];
    },
    enabled: !!tenantId,
  });
}

export function useQuickQuoteMutations() {
  const { currentTenant, user } = useAuth();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["quick-quotes", tenantId] });
  };

  const createQuote = useMutation({
    mutationFn: async (data: Partial<QuickQuote>) => {
      if (!tenantId) throw new Error("Tenant não disponível");

      const { data: codeData, error: codeError } = await supabase.rpc(
        "generate_quote_code" as any,
        { p_tenant_id: tenantId },
      );
      if (codeError) throw codeError;

      const payload = {
        ...data,
        tenant_id: tenantId,
        code: codeData as string,
        created_by: user?.id ?? null,
      };

      const { data: inserted, error } = await supabase
        .from("quick_quotes" as any)
        .insert(payload as any)
        .select()
        .single();
      if (error) throw error;
      return inserted as unknown as QuickQuote;
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["lead-quotes"] });
      toast.success("Cotação criada");
    },
    onError: (err) => {
      toast.error("Erro ao criar cotação", {
        description: (err as any)?.message || String(err),
      });
    },
  });

  const updateQuote = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<QuickQuote> }) => {
      const { data: updated, error } = await supabase
        .from("quick_quotes" as any)
        .update(data as any)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return updated as unknown as QuickQuote;
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["lead-quotes"] });
      toast.success("Cotação atualizada");
    },
    onError: (err) => {
      toast.error("Erro ao atualizar cotação", {
        description: (err as any)?.message || String(err),
      });
    },
  });

  const deleteQuote = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("quick_quotes" as any)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Cotação removida");
    },
    onError: (err) => {
      const msg = (err as any)?.message || String(err);
      const friendly =
        msg.toLowerCase().includes("policy") || msg.toLowerCase().includes("permission")
          ? "Apenas administradores podem excluir cotações"
          : msg;
      toast.error("Erro ao excluir cotação", { description: friendly });
    },
  });

  const generateSlug = useMutation({
    mutationFn: async ({
      id,
      code,
      validityDays,
    }: {
      id: string;
      code: string;
      validityDays: number;
    }) => {
      const slug =
        code.toLowerCase() + "-" + crypto.randomUUID().slice(0, 8);
      // validityDays === 0 → sem validade (expires_at NULL)
      const expiresAt =
        validityDays > 0
          ? new Date(
              Date.now() + validityDays * 24 * 60 * 60 * 1000,
            ).toISOString()
          : null;

      const { error } = await supabase
        .from("quick_quotes" as any)
        .update({
          public_slug: slug,
          sent_at: new Date().toISOString(),
          status: "sent",
          expires_at: expiresAt,
        } as any)
        .eq("id", id);
      if (error) throw error;
      return slug;
    },
    onSuccess: () => {
      invalidate();
    },
    onError: (err) => {
      toast.error("Erro ao gerar link público", {
        description: (err as any)?.message || String(err),
      });
    },
  });

  return { createQuote, updateQuote, deleteQuote, generateSlug };
}

// ─────────────── Promote Quote → Lead → Deal ───────────────

export interface PromoteQuoteResult {
  success: boolean;
  deal_id: string;
  lead_id: string;
  was_new_lead: boolean;
  deal_title: string;
  already_promoted: boolean;
}

export function usePromoteQuote() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (quote_id: string): Promise<PromoteQuoteResult> => {
      const { data, error } = await supabase.functions.invoke(
        "promote-quote-to-deal",
        { body: { quote_id } },
      );
      if (error) {
        // Try to surface server-provided message
        const ctx = (error as any)?.context;
        const serverMsg = ctx?.body?.message || ctx?.body?.error;
        throw new Error(serverMsg || error.message || "Falha ao promover cotação");
      }
      if (!data?.success) {
        throw new Error(data?.message || data?.error || "Falha ao promover cotação");
      }
      return data as PromoteQuoteResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quick-quotes", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["deals", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["leads", tenantId] });
    },
  });
}
